import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import media from '../style/media';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Flex } from '../components/shared/Flex';
import { useGetPatientByIdQuery, useInsertLicenseUsageMutation } from '../services/patientApi';
import { StorageKeys } from '../utils/storage';
import { User, Calendar, Weight, Ruler, Cigarette, Lock, Mail, Phone, Loader2, Activity } from 'lucide-react';
import { useCreateDeviceUserMutation } from '../services/patientApi';
import { useGetOrganizationByIdQuery } from '../services/organizationApi';

import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard } from '../style/layout';

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  width: 100%;
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  padding-left: 0.25rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 1rem;
    color: #94a3b8;
    pointer-events: none;
    z-index: 10;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.875rem 0.875rem 0.875rem 3rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  color: #0f172a;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.875rem 0.875rem 0.875rem 3rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  color: #0f172a;
  appearance: none;
  cursor: pointer;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
`;

const PrivacyNote = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  font-size: 0.85rem;
  color: #475569;
  background: #f8fafc;
  padding: 1rem;
  border-radius: 16px;
  margin: 1.5rem 0;
  line-height: 1.5;
  border: 1px solid #f1f5f9;

  svg {
    margin-top: 2px;
  }
`;

const StartButton = styled.button<{ isComplete: boolean }>`
  width: 100%;
  padding: 1rem;
  background: ${props => props.isComplete
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : '#e2e8f0'};
  color: white;
  border: none;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 700;
  cursor: ${props => props.isComplete ? 'pointer' : 'not-allowed'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.isComplete ? '0 4px 12px rgba(102, 126, 234, 0.25)' : 'none'};

  &:hover {
    ${props => props.isComplete && `
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.35);
      opacity: 0.95;
    `}
  }

  &:active {
    ${props => props.isComplete && `
      transform: translateY(0);
    `}
  }
`;

const FullScan = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        id_number: '',
        gender: '',
        age: '',
        weight: '',
        height: '',
        smoking: '',
        diabetic: '',
        bp: '',
        other_health_issues: ''
    });

    const [scanFor, setScanFor] = useState<'self' | 'other'>('self');
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isLicenseValid, setIsLicenseValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const cameras = useCameras();
    const [createDeviceUser] = useCreateDeviceUserMutation();
    const [insertLicenseUsage] = useInsertLicenseUsageMutation();

    const currentOrgId = useMemo(() => {
        const orgId = localStorage.getItem(StorageKeys.ORG_ID);
        return (orgId === 'null' || orgId === 'undefined' || !orgId) ? '' : orgId;
    }, []);
    const currentPatientId = useMemo(() => {
        const patientId = localStorage.getItem(StorageKeys.PATIENT_ID);
        return (patientId === 'null' || patientId === 'undefined' || !patientId) ? undefined : patientId;
    }, []);
    const { data: orgData } = useGetOrganizationByIdQuery(currentOrgId, { skip: !currentOrgId });
    const { data: patientData } = useGetPatientByIdQuery(currentPatientId || '', { skip: !currentPatientId });

    const requiredFieldsSelf = ['gender', 'age', 'weight', 'height', 'smoking', 'diabetic', 'bp'];
    const requiredFieldsOther = ['name', 'email', 'phone', ...requiredFieldsSelf];

    const isFormComplete = scanFor === 'self'
        ? requiredFieldsSelf.every(k => formData[k as keyof typeof formData] !== '')
        : requiredFieldsOther.every(k => formData[k as keyof typeof formData] !== '');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFullScan = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            let deviceUserId: string | undefined;
            let organizationId = currentOrgId;

            const payloadExt = {
                age: Number(formData.age),
                sex: formData.gender === 'MALE' ? 'Male' : formData.gender === 'FEMALE' ? 'Female' : 'Unspecified',
                diabetic: formData.diabetic === 'yes',
                bp: formData.bp === 'yes',
                other_health_issues: formData.other_health_issues,
                height: Number(formData.height),
                weight: Number(formData.weight),
                smoking_status: formData.smoking === 'SMOKER' ? 'Smoker' : formData.smoking === 'NON_SMOKER' ? 'Non-smoker' : 'Unspecified'
            };

            const isPatientSelfScan = !!currentPatientId && scanFor === 'self';

            if (isPatientSelfScan) {
                deviceUserId = undefined;
                localStorage.removeItem(StorageKeys.DEVICE_USER_ID);
            } else if (scanFor === 'other') {
                // 1. Create Device User
                const deviceUserResponse = await createDeviceUser({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    id_number: formData.id_number,
                    patient_id: currentPatientId,
                    ...payloadExt
                }).unwrap();

                if (deviceUserResponse.success) {
                    deviceUserId = deviceUserResponse.data.id.toString();
                    organizationId = deviceUserResponse.data.organization_id;

                    // Store device user id
                    localStorage.setItem(StorageKeys.DEVICE_USER_ID, deviceUserId);
                }
            } else {
                // For organization self-scan, create a device user using organization details
                const orgName = orgData?.organization?.name || 'Organization User';
                const orgEmail = orgData?.organization?.email || '';
                const orgPhone = orgData?.organization?.phone || '';

                const deviceUserResponse = await createDeviceUser({
                    name: orgName,
                    email: orgEmail,
                    phone: orgPhone,
                    patient_id: currentPatientId,
                    ...payloadExt
                }).unwrap();

                if (deviceUserResponse.success) {
                    deviceUserId = deviceUserResponse.data.id.toString();
                    organizationId = deviceUserResponse.data.organization_id;

                    // Store device user id
                    localStorage.setItem(StorageKeys.DEVICE_USER_ID, deviceUserId);
                }
            }

            // 2. Get License ID from organization data or patient data
            const licenseId = patientData?.patient?.license_id || orgData?.organization?.license_id;
            const accessCode = patientData?.patient?.access_code;

            if (licenseId) {
                // 3. Insert License Usage
                let usagePayload: any;
                if (isPatientSelfScan) {
                    usagePayload = {
                        license_id: licenseId,
                        patient_id: currentPatientId,
                        access_code: accessCode,
                    };
                } else {
                    usagePayload = {
                        license_id: licenseId,
                    };
                    if (!currentPatientId) {
                        usagePayload.organization_id = organizationId;
                    }
                    if (deviceUserId) {
                        usagePayload.device_user_id = deviceUserId;
                    }
                }
                const usageResponse = await insertLicenseUsage(usagePayload).unwrap();

                // 4. Store license usage id in local storage
                if (usageResponse?.licenseUsage?.id) {
                    localStorage.setItem(StorageKeys.LICENSE_USAGE_ID, usageResponse.licenseUsage.id);
                }
            }

            // Navigate to camera scan with all collected data
            navigate('/camera-scan', { state: { ...formData, isEnhanced: true, isFullScan: true, scanFor } });
        } catch (error) {
            console.error('Error in full scan submission:', error);
            // Even if license usage fails, we might want to proceed with the scan 
            // but usually it's better to show an error if the setup fails.
            navigate('/camera-scan', { state: { ...formData, isEnhanced: true, isFullScan: true, scanFor } });
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleSettings = useCallback(() => {
        setIsSettingsOpen(prev => !prev);
    }, []);

    const handleCloseSettings = useCallback(() => {
        setIsSettingsOpen(false);
    }, []);

    const updateLicenseStatus = useCallback((valid: boolean) => {
        setIsLicenseValid(valid);
    }, []);

    return (
        <PageContainer>
            <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />

            <SettingsBars
                open={isSettingsOpen}
                onClose={handleCloseSettings}
                cameras={cameras}
                isLicenseValid={isLicenseValid}
            />

            <ContentWrapper>
                <GlassCard>
                    <Header>
                        <Title>Scan Details</Title>
                        <Subtitle>Enter details for a comprehensive health assessment</Subtitle>
                    </Header>

                    <form onSubmit={handleFullScan}>
                        <FormField>
                            <FormLabel>Scan For</FormLabel>
                            <InputWrapper>
                                <User />
                                <FormSelect
                                    value={scanFor}
                                    onChange={(e) => setScanFor(e.target.value as 'self' | 'other')}
                                >
                                    <option value="self">Self</option>
                                    <option value="other">Others</option>
                                </FormSelect>
                            </InputWrapper>
                        </FormField>

                        {scanFor === 'other' && (
                            <>
                                <FormField>
                                    <FormLabel>Full Name</FormLabel>
                                    <InputWrapper>
                                        <User />
                                        <FormInput
                                            type="text"
                                            name="name"
                                            placeholder="Enter full name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required={scanFor === 'other'}
                                        />
                                    </InputWrapper>
                                </FormField>

                                <FormField>
                                    <FormLabel>Email</FormLabel>
                                    <InputWrapper>
                                        <Mail />
                                        <FormInput
                                            type="email"
                                            name="email"
                                            placeholder="Enter email address"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required={scanFor === 'other'}
                                        />
                                    </InputWrapper>
                                </FormField>

                                <FormField>
                                    <FormLabel>Phone Number</FormLabel>
                                    <InputWrapper>
                                        <Phone />
                                        <FormInput
                                            type="tel"
                                            name="phone"
                                            placeholder="Enter phone number"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required={scanFor === 'other'}
                                        />
                                    </InputWrapper>
                                </FormField>

                                <FormField>
                                    <FormLabel>ID Number</FormLabel>
                                    <InputWrapper>
                                        <User />
                                        <FormInput
                                            type="text"
                                            name="id_number"
                                            placeholder="Enter ID number (optional)"
                                            value={formData.id_number}
                                            onChange={handleInputChange}
                                        />
                                    </InputWrapper>
                                </FormField>
                            </>
                        )}

                        <FormField>
                            <FormLabel>Diabetic</FormLabel>
                            <InputWrapper>
                                <Activity />
                                <FormSelect
                                    name="diabetic"
                                    value={formData.diabetic}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Are you diabetic?</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                </FormSelect>
                            </InputWrapper>
                        </FormField>

                        <FormField>
                            <FormLabel>Blood Pressure (BP)</FormLabel>
                            <InputWrapper>
                                <Activity />
                                <FormSelect
                                    name="bp"
                                    value={formData.bp}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Do you have BP issues?</option>
                                    <option value="yes">Yes</option>
                                    <option value="no">No</option>
                                </FormSelect>
                            </InputWrapper>
                        </FormField>

                        <FormField>
                            <FormLabel>Other Health Issues</FormLabel>
                            <InputWrapper>
                                <Activity />
                                <FormInput
                                    type="text"
                                    name="other_health_issues"
                                    placeholder="Enter any other health issues (optional)"
                                    value={formData.other_health_issues}
                                    onChange={handleInputChange}
                                />
                            </InputWrapper>
                        </FormField>

                        <FormField>
                            <FormLabel>Gender</FormLabel>
                            <InputWrapper>
                                <User />
                                <FormSelect
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Gender</option>
                                    <option value="MALE">Male</option>
                                    <option value="FEMALE">Female</option>
                                    {/* <option value="UNSPECIFIED">Prefer not to say</option> */}
                                </FormSelect>
                            </InputWrapper>
                        </FormField>

                        <FormField>
                            <FormLabel>Age (years)</FormLabel>
                            <InputWrapper>
                                <Calendar />
                                <FormInput
                                    type="number"
                                    name="age"
                                    min="18"
                                    max="120"
                                    placeholder="Enter age"
                                    value={formData.age}
                                    onChange={handleInputChange}
                                    required
                                />
                            </InputWrapper>
                        </FormField>

                        <FormField>
                            <FormLabel>Weight (kg)</FormLabel>
                            <InputWrapper>
                                <Weight />
                                <FormInput
                                    type="number"
                                    name="weight"
                                    min="30"
                                    max="300"
                                    placeholder="Enter weight in kg"
                                    value={formData.weight}
                                    onChange={handleInputChange}
                                    required
                                />
                            </InputWrapper>
                        </FormField>

                        <FormField>
                            <FormLabel>Height (cm)</FormLabel>
                            <InputWrapper>
                                <Ruler />
                                <FormInput
                                    type="number"
                                    name="height"
                                    min="100"
                                    max="250"
                                    placeholder="Enter height in cm"
                                    value={formData.height}
                                    onChange={handleInputChange}
                                    required
                                />
                            </InputWrapper>
                        </FormField>

                        <FormField>
                            <FormLabel>Smoking Status</FormLabel>
                            <InputWrapper>
                                <Cigarette />
                                <FormSelect
                                    name="smoking"
                                    value={formData.smoking}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Do you smoke?</option>
                                    <option value="NON_SMOKER">Non-Smoker</option>
                                    <option value="SMOKER">Smoker</option>
                                    {/* <option value="UNSPECIFIED">Prefer not to say</option> */}
                                </FormSelect>
                            </InputWrapper>
                        </FormField>



                        <PrivacyNote>
                            <Lock size={18} />
                            <span>
                                <strong>Confidential:</strong> Your data is used exclusively for heart age analysis and ASCVD risk scoring.
                            </span>
                        </PrivacyNote>

                        <StartButton
                            type="submit"
                            disabled={!isFormComplete || isSubmitting}
                            isComplete={isFormComplete && !isSubmitting}
                        >
                            {isSubmitting ? (
                                <Flex gap="0.5rem" align="center" justify="center">
                                    <Loader2 className="animate-spin" size={20} />
                                    <span>Processing...</span>
                                </Flex>
                            ) : (
                                'Start Full Scan'
                            )}
                        </StartButton>
                    </form>
                </GlassCard>
            </ContentWrapper>
        </PageContainer>
    );
};

export default FullScan;
